import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ICertificateQuery } from 'app/library/certificate/interfaces/certificate.query.interface';
import { Certificate } from 'app/library/certificate/models/certificate.model';
import { SimpleCertificate } from 'app/library/certificate/models/simple-certificate.model';
import { FinalGradeCertificate, ISimpleCertificate } from 'app/library/certificate/services/certificate.api-service';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { FileCommand } from 'app/projects/core/src/lib/models/file.command';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CreatePrivateModuleCertificateActionPayload, CreatePublicModuleCertificateActionPayload } from '../actions';
import { IManageModuleUserCertificatesActionPayload } from '../interfaces/module-certificate-action.action-payload.interface';
import { IManagePostModuleCertificateActionPayload } from '../interfaces/module-certificate-action.interface';
import { IModuleCertificateQuery } from '../interfaces/module-certificate.query.interface';
import { ModuleCertificateCommand } from '../models/module-certificate.command.model';
import { ModuleCertificateExtended } from '../models/module-certificate.extended.model';
import { UploadPrivateCertificateCommand } from '../models/upload-private-certificate.command';

@Injectable()
export class ModuleCertificateApiService extends EntityService<IModuleCertificateQuery, ModuleCertificateCommand> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment) {
        super(http, environment.apiUrl, 'certificate/modules');
    }

    getByModuleId(moduleId: string): Observable<ModuleCertificateExtended> {
        return this.http.get<IModuleCertificateQuery>(`${this.apiUrl}/${this.entities}/${moduleId}/template`).pipe(map((query) => new ModuleCertificateExtended(query)));
    }

    assignPrivate({ moduleId, file, assignmentRule }: CreatePrivateModuleCertificateActionPayload): Observable<ModuleCertificateExtended> {
        const data = new UploadPrivateCertificateCommand(assignmentRule, file).formData;

        return this.http
            .post<IModuleCertificateQuery>(`${this.apiUrl}/${this.entities}/${moduleId}/assign-private-template`, data)
            .pipe(map((query) => new ModuleCertificateExtended(query)));
    }

    assignPublic({ moduleId, certificate, assignmentRule }: CreatePublicModuleCertificateActionPayload): Observable<ModuleCertificateExtended> {
        const data = {
            template: {
                id: certificate.id,
            },
            assignment_rule: assignmentRule,
        };

        return this.http
            .post<IModuleCertificateQuery>(`${this.apiUrl}/${this.entities}/${moduleId}/assign-public-template`, data)
            .pipe(map((query) => new ModuleCertificateExtended(query)));
    }

    read(id: string): Observable<ModuleCertificateExtended> {
        return this._read(id).pipe(map((query) => new ModuleCertificateExtended(query)));
    }

    update(entity: ModuleCertificateExtended): Observable<ModuleCertificateExtended> {
        const data = new ModuleCertificateCommand(entity);

        return this.http.patch<IModuleCertificateQuery>(`${this.apiUrl}/${this.entities}-templates/${entity.id}`, data).pipe(map((query) => new ModuleCertificateExtended(query)));
    }

    deleteModuleCertificate(id: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/certificate/templates/${id}`);
    }

    downloadModuleTemplate(id: string, fileName: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/certificate/templates/${id}`, { responseType: 'blob' }).pipe(tap((file) => MaxBrainUtils.downloadFile(file, fileName)));
    }

    fetchParticipantModuleCertificates(moduleId: string): Observable<SimpleCertificate[]> {
        return this.http
            .get<ISimpleCertificate[]>(`${this.apiUrl}/certificate-of-attendance/modules/${moduleId}/certificates`)
            .pipe(map((queries) => queries.map((query) => new SimpleCertificate(query))));
    }

    fetchModuleUsedTemplates(moduleId: string): Observable<SimpleCertificate[]> {
        return this.http
            .get<ISimpleCertificate[]>(`${this.apiUrl}/certificate-of-attendance/modules/${moduleId}/used-templates`)
            .pipe(map((queries) => queries.map((query) => new SimpleCertificate(query))));
    }

    updateModuleCertificateTemplate(id: string, certificateName: string): Observable<Certificate> {
        const data = { certificate_name: certificateName };

        return this.http.patch<ICertificateQuery>(`${this.apiUrl}/certificate/templates/${id}`, data).pipe(map((query) => new Certificate(query)));
    }

    assignCertificateTemplateToModule(moduleId: string, certificateName: string, payload: File, assignmentRule: number, sectionId?: string): Observable<ModuleCertificateExtended> {
        const data = new FileCommand('file', new File([payload], '', { type: 'text/plain' }), payload.name).formData;
        data.append('certificate_name', certificateName);
        data.append('assignment_rule', assignmentRule.toString());

        if (sectionId) {
            data.append('section_id', sectionId);
        }

        return this.http
            .post<IModuleCertificateQuery>(`${this.apiUrl}/certificate-of-attendance/modules/${moduleId}/assign-private-template`, data)
            .pipe(map((query) => new ModuleCertificateExtended(query)));
    }

    assignCertificatesToModuleUsers({
        moduleUserIds,
        moduleTemplateIds,
        sendNotification,
    }: IManageModuleUserCertificatesActionPayload): Observable<{ existing: FinalGradeCertificate[]; newlyAssigned: FinalGradeCertificate[] }> {
        const data = {
            module_user_ids: moduleUserIds,
            module_template_ids: moduleTemplateIds,
        };

        let params = new HttpParams().set('send_notification', sendNotification.toString());

        return this.http.post<IManagePostModuleCertificateActionPayload>(`${this.apiUrl}/certificate-of-attendance/certificates`, data, { params }).pipe(
            map((queries) => {
                return {
                    existing: queries.existing.map((query) => new FinalGradeCertificate(query)), // TODO: change model
                    newlyAssigned: queries.new.map((query) => new FinalGradeCertificate(query)), // TODO: change model
                };
            })
        );
    }

    unassignCertificatesFromModuleUsers({ moduleUserIds, certificateIds }: IManageModuleUserCertificatesActionPayload): Observable<{ removed: number }> {
        const body: { module_user_ids?: number[]; certificate_ids?: number[] } = {};

        if (certificateIds?.length) {
            body.certificate_ids = certificateIds.map((id) => parseInt(id));
        } else if (moduleUserIds?.length) {
            body.module_user_ids = moduleUserIds.map((id) => parseInt(id));
        }

        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body,
        };

        return this.http.delete<{ removed: number }>(`${this.apiUrl}/certificate/certificates/bulk`, options);
    }

    deleteCertificate(id: string): Observable<any> {
        return this.http.delete<void>(`${this.apiUrl}/certificate/certificates/${id}`);
    }

    previewCertificatePdf(id: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/${this.entities}-templates/${id}/preview-certificate`, { responseType: 'blob' });
    }

    previewCertificationPdf(id: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/certificates/module-certifications/${id}/preview-certificate`, { responseType: 'blob' });
    }

    fetchCertificateKeywords(): Observable<string[]> {
        return this.http.get<string[]>(`${this.apiUrl}/certificate-of-attendance/keywords`);
    }
}
